import { cx } from '@emotion/css';
import { ButtonBase, Typography } from '@mui/material';
import {
  type NodeProps,
  type ReactFlowState,
  useStore,
  useViewport,
} from '@xyflow/react';
import { makeStyles } from 'tss-react/mui';

import { InlineTextEditor } from '@/components/diagrams/components/InlineTextEditor/InlineTextEditor';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { COLORS } from '@/styles/tokens/colors';

import { useDiagramEditing } from '../../contexts/diagramEditing.context';
import {
  getDividerStageID,
  STAGE_DIVIDER_DRAG_HANDLE,
  type StageDividerNode,
  type StageHighlightNode,
} from '../../utils/diagramStages';

const canvasWidthSelector = ({ width }: ReactFlowState) => width;

/** Clear space between the label and the left edge of the visible canvas. */
const LABEL_LEFT_MARGIN = 16;

/** The height of the area around the line that the user drags, in screen pixels. */
const DRAG_HANDLE_HEIGHT = 12;

const useStyles = makeStyles()(() => ({
  root: { position: 'absolute', inset: 0, pointerEvents: 'none' },
  line: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  handle: {
    pointerEvents: 'all',
    cursor: 'row-resize',
    '&:hover > div, &:active > div': { borderTopColor: COLORS.GRAY[500] },
  },
  rule: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    borderTop: `1px dashed ${COLORS.GRAY[300]}`,
  },
  chip: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 6px 2px 10px',
    borderRadius: 4,
    // Opaque background so the dashed rule does not strike through the label.
    backgroundColor: COLORS.GRAY[50],
    pointerEvents: 'all',
    '&:hover': { backgroundColor: COLORS.GRAY[100] },
    '&:not(:hover) .stage-divider-delete': { visibility: 'hidden' },
  },
  label: { color: COLORS.GRAY[500], whiteSpace: 'nowrap' },
  editor: { minWidth: 120 },
  placeholder: { color: COLORS.GRAY[400], fontStyle: 'italic' },
  highlight: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    backgroundColor: COLORS.BLUE[100],
    opacity: 0.45,
  },
}));

/**
 * Returns the left edge and width of the visible canvas, in the flow units of
 * a node at `positionAbsoluteX`. Before React Flow measures the canvas, they
 * are the node box.
 */
function useVisibleCanvas(positionAbsoluteX: number, width?: number) {
  const canvasWidth = useStore(canvasWidthSelector);
  const { x: viewportX, zoom } = useViewport();
  if (canvasWidth <= 0) return { left: 0, width: width ?? 0 };
  return {
    left: -viewportX / zoom - positionAbsoluteX,
    width: canvasWidth / zoom,
  };
}

/**
 * The divider before a stage of the builder canvas: a dashed line across the
 * visible canvas, with a label at its left. The user drags the line to move
 * the divider. A click on the label edits it in place, and the close button
 * removes the divider. A divider without actions is not interactive.
 *
 * It looks like the shared phase band of presentation diagrams, but it is a
 * builder control, so it is a separate node type.
 */
export function DiagramStageDividerNode({
  id,
  data: { label, onEdit, onDelete },
  width,
  positionAbsoluteX,
}: NodeProps<StageDividerNode>) {
  const { classes } = useStyles();
  const canvas = useVisibleCanvas(positionAbsoluteX, width);
  const { zoom } = useViewport();
  const { labelTarget, closeLabelEditor, editStageLabel } = useDiagramEditing();
  // Each divider node ID has the prefix and the ID of the stage after it.
  const stageID = getDividerStageID(id)!;
  const text = (
    <Typography
      variant="body-xs"
      className={cx(classes.label, !label && classes.placeholder)}
    >
      {label || (onEdit ? 'Add label' : '')}
    </Typography>
  );
  const isEditing =
    !!onEdit && labelTarget?.kind === 'stage' && labelTarget.id === stageID;
  let labelControl = text;
  if (isEditing) {
    labelControl = (
      <Typography variant="body-xs" component="div" className={classes.label}>
        <InlineTextEditor
          value={label}
          label="Stage label"
          className={classes.editor}
          onCommit={(value) => {
            closeLabelEditor();
            // A blank label is valid. The divider stays, with a placeholder.
            if (value.trim() !== label) editStageLabel(stageID, value.trim());
          }}
          onCancel={closeLabelEditor}
        />
      </Typography>
    );
  } else if (onEdit) {
    labelControl = (
      <ButtonBase onClick={onEdit} aria-label="Edit stage label">
        {text}
      </ButtonBase>
    );
  }

  return (
    <div className={classes.root} data-testid="stage-divider">
      <div
        className={cx(
          classes.line,
          onEdit && [classes.handle, STAGE_DIVIDER_DRAG_HANDLE]
        )}
        style={{
          left: canvas.left,
          width: canvas.width,
          // The handle keeps the same screen height at every zoom.
          height: DRAG_HANDLE_HEIGHT / zoom,
        }}
      >
        <div className={classes.rule} />
      </div>
      {(label || onEdit) && (
        <div
          // `nodrag` and `nopan` stop React Flow from using a click on the
          // label to drag or pan.
          className={cx(classes.chip, onEdit && ['nodrag', 'nopan'])}
          style={{ left: canvas.left + LABEL_LEFT_MARGIN }}
        >
          {labelControl}
          {onDelete && !isEditing && (
            <ButtonBase
              className="stage-divider-delete"
              onClick={onDelete}
              aria-label="Remove stage divider"
            >
              <XCloseIcon size={12} color={COLORS.GRAY[500]} />
            </ButtonBase>
          )}
        </div>
      )}
    </div>
  );
}

/** Tints the stage that a dragged tile goes to, across the visible canvas. */
export function DiagramStageHighlightNode({
  width,
  positionAbsoluteX,
}: NodeProps<StageHighlightNode>) {
  const { classes } = useStyles();
  const canvas = useVisibleCanvas(positionAbsoluteX, width);
  return (
    <div
      className={classes.highlight}
      data-testid="stage-highlight"
      style={{ left: canvas.left, width: canvas.width }}
    />
  );
}
